<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import WorkOrderManagementDetail from './components/WorkOrderManagementDetail'
import { getWorkOrderPlan, updateWorkOrderPlan } from './services/workOrderPlans'

export default {
  page: {
    title: 'WO Board Detail',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    WorkOrderManagementDetail,
  },
  data() {
    return {
      workOrder: null,
      isLoading: false,
      isUpdatingStatus: false,
      isUpdatingSchedule: false,
    }
  },
  computed: {
    title() {
      return 'WO Detail'
    },
    woId() {
      return this.$route.params.woNumber
    },
    woNumber() {
      return this.workOrder ? this.workOrder.wo_number : this.woId
    },
    metadata() {
      if (!this.workOrder) return []

      return [
        { label: 'WO Number', value: this.workOrder.wo_number },
        { label: 'Customer', value: this.workOrder.customer },
        { label: 'City', value: this.workOrder.city },
        { label: 'DT Number', value: this.workOrder.dt_number },
        { label: 'Status', value: this.statusText, type: 'status' },
        { label: 'Plan Date', value: this.workOrder.plan_datetime, type: 'datetime' },
        { label: 'Estimated Time', value: this.workOrder.estimated_time, type: 'datetime' },
        { label: 'Technician Qty', value: this.workOrder.technician_qty },
        { label: 'Confirmed', value: this.workOrder.is_plan_confirmed ? 'Yes' : 'No' },
        { label: 'Latitude', value: this.workOrder.latitude },
        { label: 'Longitude', value: this.workOrder.longitude },
        { label: 'Created By', value: this.workOrder.created_by },
      ]
    },
    statusText() {
      if (!this.workOrder) return '-'

      return this.getStatusText(this.workOrder.status)
    },

  },
  created() {
    this.fetchWorkOrderPlan()
  },
  methods: {
    normalizeResponseData(response) {
      const payload = response.data
      const data = payload && payload.data
      let item = payload

      if (Array.isArray(payload)) item = payload[0]
      else if (Array.isArray(data)) item = data[0]
      else if (data && Array.isArray(data.paginated_result)) item = data.paginated_result[0]
      else if (data && Array.isArray(data.data)) item = data.data[0]
      else if (data && typeof data === 'object') item = data

      if (!item) return null

      return {
        ...item,
        id: item.id || item.work_order_plan_id,
        status: this.getStatusText(item.status || item.status_name || item.work_order_status || item.work_order_plan_status),
      }
    },
    getErrorMessage(error, fallback) {
      if (error.response && error.response.data) {
        return error.response.data.error || error.response.data.message || fallback
      }

      return error.message || fallback
    },
    async fetchWorkOrderPlan() {
      this.isLoading = true
      try {
        const response = await getWorkOrderPlan(this.woId)
        this.workOrder = this.normalizeResponseData(response)
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Failed loading WO plan'))
      } finally {
        this.isLoading = false
      }
    },
    getStatusText(status) {
      if (!status) return '-'
      if (typeof status === 'string') return status

      return status.name || status.status || status.title || status.label || status.value || '-'
    },
    async updateStatus(status) {
      if (!this.workOrder || !this.workOrder.id || !status || this.isUpdatingStatus) return

      this.isUpdatingStatus = true

      try {
        const response = await updateWorkOrderPlan(this.workOrder.id, { status })
        const updatedWorkOrder = this.normalizeResponseData(response)

        this.workOrder = {
          ...this.workOrder,
          ...updatedWorkOrder,
          status: this.getStatusText((updatedWorkOrder && updatedWorkOrder.status) || status),
        }
        await this.fetchWorkOrderPlan()
        this.$toast.success(`WO status updated to ${status}`)
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Failed updating WO status'))
      } finally {
        this.isUpdatingStatus = false
      }
    },
    async updateSchedule(payload) {
      if (!this.workOrder || !this.workOrder.id || this.isUpdatingSchedule) return

      this.isUpdatingSchedule = true

      try {
        await updateWorkOrderPlan(this.workOrder.id, payload)
        await this.fetchWorkOrderPlan()
        this.$toast.success('WO schedule updated')
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Failed updating WO schedule'))
      } finally {
        this.isUpdatingSchedule = false
      }
    },
    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString()
    },
  },
}
</script>

<template>
  <Layout>
    <div class="wo-detail-page">
      <div v-if="isLoading" class="wo-detail-loading">
        <b-spinner small class="mr-2" />
        Loading work order...
      </div>
      <WorkOrderManagementDetail
        v-else
        :work-order="workOrder"
        :is-updating-status="isUpdatingStatus"
        :is-updating-schedule="isUpdatingSchedule"
        @update-status="updateStatus"
        @update-schedule="updateSchedule"
      />
    </div>
  </Layout>
</template>

<style scoped>
.wo-detail-page {
  color: #344054;
}

.wo-detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 28px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  color: #667085;
  font-size: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

</style>
